// src/components/common/CreditsDisplay.tsx
import React, { useState } from 'react';
import { useAppSelector } from '../../app/hooks';
import { Box, Typography, IconButton, Popover, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import TollIcon from '@mui/icons-material/Toll';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const CreditsDisplay: React.FC = () => {
    const { t } = useTranslation();
    const user = useAppSelector((state) => state.auth.user);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    // Don't render if the user object or credits are not available.
    if (!user || typeof user.credits === 'undefined') {
        return null;
    }

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: 'action.hover',
                    borderRadius: '20px',
                    px: 1.5,
                    py: 0.5,
                    height: '32px',
                    cursor: 'pointer',
                    width: '120px', // NEW: Set fixed width to accommodate 5-digit numbers
                    justifyContent: 'space-between', // NEW: Distribute elements across the box
                }}
                aria-owns={open ? 'mouse-over-popover' : undefined}
                aria-haspopup="true"
                onMouseEnter={handlePopoverOpen}
                onMouseLeave={handlePopoverClose}
            >
                <TollIcon sx={{ color: 'primary.main', fontSize: '1.25rem' }} /> {/* Left aligned */}
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 'bold',
                        flexGrow: 1, // NEW: Allow text to take available space
                        textAlign: 'center', // NEW: Center the credits number
                    }}
                >
                    {user.credits}
                </Typography>
                <IconButton size="small" sx={{ pointerEvents: 'none' }}>
                    <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} /> {/* Right aligned */}
                </IconButton>
            </Box>
            <Popover
                id="mouse-over-popover"
                sx={{
                    pointerEvents: 'none',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            pointerEvents: 'auto',
                            // --- FIX START: Adjust padding and margins for better aesthetics ---
                            py: 3,      // Increased to 24px vertical padding for more spacious feel
                            px: 4,      // Increased to 32px horizontal padding for better text spacing
                            // --- FIX END ---
                            maxWidth: 360, // Slightly wider to accommodate content comfortably
                            borderRadius: 4, // Increased border radius for more rounded, modern look
                            boxShadow: '0px 8px 24px rgba(0,0,0,0.12)', // A softer, more modern shadow
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            mt: 1.5, // Slightly increased margin-top for better separation
                            backdropFilter: 'blur(10px)',
                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(44, 44, 46, 0.85)' : 'rgba(255, 255, 255, 0.85)',
                            overflow: 'hidden', // Ensures inner elements respect the border radius
                        },
                        onMouseEnter: handlePopoverOpen,
                        onMouseLeave: handlePopoverClose,
                    }
                }}
                open={open}
                anchorEl={anchorEl}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                disableRestoreFocus
            >
                <Typography variant="h6" gutterBottom>{t('credits_info_title', { ns: 'common' })}</Typography>
                {/* --- FIX: Adjusted divider margin for better vertical rhythm --- */}
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2" color="text.secondary">
                    {t('credits_info_desc', { ns: 'common' })}
                </Typography>
            </Popover>
        </>
    );
};

export default CreditsDisplay;